import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { saveContactMessage } from '../lib/supabase';

// Google Form field IDs - match the ones in PublicationDetail
const GOOGLE_FORM = {
  ID: '1eLsgAa7nXlwSXHY4N8klZsFdRk2b5o4CQFQYSoFOS_Y',
  FIELDS: {
    EMAIL: 'entry.1045781291',
    RESEARCH_INTERESTS: 'entry.1234567891',
    PROFESSIONAL_BACKGROUND: 'entry.1234567892',
    HEARD_ABOUT_US: 'entry.1234567893'
  }
} as const;

const RESEARCH_INTERESTS = [
  'Foundational Histories',
  'Governance and Leadership',
  'Culture and Heritage',
  'Urbanization and Development',
  'Conflicts and Wars'
] as const;

const PROFESSIONAL_BACKGROUNDS = [
  'Student',
  'Academic/Researcher',
  'Historian',
  'Cultural Enthusiast',
  'Other'
] as const;

const HEARD_ABOUT_US_OPTIONS = [
  'Academic Reference',
  'Social Media',
  'Search Engine',
  'Word of Mouth',
  'Other'
] as const;

interface FormData {
  email: string;
  researchInterests: string[];
  professionalBackground: string;
  heardAboutUs: string;
  message: string;
}

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<FormData>({
    email: '',
    researchInterests: [],
    professionalBackground: '',
    heardAboutUs: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const subject = searchParams.get('subject');
    if (subject) {
      setFormData(prev => ({
        ...prev,
        message: `Regarding: ${subject}\n\n`
      }));
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    
    try {
      // Create form data for Google Forms
      const formElement = document.createElement('form');
      formElement.method = 'POST';
      formElement.action = `https://docs.google.com/forms/d/e/${GOOGLE_FORM.ID}/formResponse`;
      formElement.target = '_blank'; // Open in new tab
      formElement.style.display = 'none';

      // Add form fields
      const fields = {
        [GOOGLE_FORM.FIELDS.EMAIL]: formData.email,
        [GOOGLE_FORM.FIELDS.RESEARCH_INTERESTS]: formData.researchInterests.join(', '),
        [GOOGLE_FORM.FIELDS.PROFESSIONAL_BACKGROUND]: formData.professionalBackground,
        [GOOGLE_FORM.FIELDS.HEARD_ABOUT_US]: formData.heardAboutUs
      };

      // Create hidden inputs for each field
      Object.entries(fields).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        formElement.appendChild(input);
      });

      // Also save to Supabase
      await saveContactMessage({
        email: formData.email,
        professionalBackground: formData.professionalBackground,
        researchInterests: formData.researchInterests.join(', '),
        heardAboutUs: formData.heardAboutUs,
        message: formData.message
      });

      // Submit form to Google Forms
      document.body.appendChild(formElement);
      formElement.submit();
      document.body.removeChild(formElement);

      setStatus('success');
      setFormData({
        email: '',
        researchInterests: [],
        professionalBackground: '',
        heardAboutUs: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending message:', error);
      setStatus('error');
      setErrorMessage('Failed to send message. Please try again later.');
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5DC] pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-[#064635] mb-4">Contact Us</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Have questions about our historical records? Want to contribute to our archives? 
            We'd love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-[#064635] mb-4">Contact Information</h3>
                <p className="text-gray-600 mb-6">
                  Get in touch with our team for any inquiries about our historical records or research opportunities.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start">
                  <Mail className="h-6 w-6 text-[#D4AF37] mt-1" />
                  <div className="ml-4">
                    <p className="font-medium text-[#064635]">Email</p>
                    <a href="mailto:info@echoesofibadan.org" className="text-gray-600 hover:text-[#D4AF37]">
                      info@echoesofibadan.org
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <Phone className="h-6 w-6 text-[#D4AF37] mt-1" />
                  <div className="ml-4">
                    <p className="font-medium text-[#064635]">Phone</p>
                    <p className="text-gray-600">+234 (0) 123 456 7890</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <MapPin className="h-6 w-6 text-[#D4AF37] mt-1" />
                  <div className="ml-4">
                    <p className="font-medium text-[#064635]">Address</p>
                    <p className="text-gray-600">
                      University of Ibadan<br />
                      Ibadan, Oyo State<br />
                      Nigeria
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Research Interests
                  </label>
                  <div className="space-y-2">
                    {RESEARCH_INTERESTS.map((interest) => (
                      <label key={interest} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.researchInterests.includes(interest)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                researchInterests: [...prev.researchInterests, interest]
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                researchInterests: prev.researchInterests.filter(i => i !== interest)
                              }));
                            }
                          }}
                          className="rounded border-gray-300 text-[#D4AF37] focus:ring-[#D4AF37]"
                        />
                        <span className="ml-2 text-gray-700">{interest}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="professionalBackground" className="block text-sm font-medium text-gray-700 mb-1">
                    Professional Background *
                  </label>
                  <select
                    id="professionalBackground"
                    required
                    value={formData.professionalBackground}
                    onChange={(e) => setFormData(prev => ({ ...prev, professionalBackground: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  >
                    <option value="">Select an option</option>
                    {PROFESSIONAL_BACKGROUNDS.map((background) => (
                      <option key={background} value={background}>{background}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="heardAboutUs" className="block text-sm font-medium text-gray-700 mb-1">
                    How did you hear about us?
                  </label>
                  <select
                    id="heardAboutUs"
                    value={formData.heardAboutUs}
                    onChange={(e) => setFormData(prev => ({ ...prev, heardAboutUs: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  >
                    <option value="">Select an option</option>
                    {HEARD_ABOUT_US_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                    placeholder="Tell us about your research interests or how we can help you..."
                  ></textarea>
                </div>

                {status === 'error' && (
                  <div className="text-red-600 text-sm">{errorMessage}</div>
                )}

                {status === 'success' && (
                  <div className="bg-green-50 text-green-800 rounded-lg p-4">
                    Thank you for your message! We'll get back to you soon.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full bg-[#064635] text-white px-6 py-3 rounded-lg font-semibold 
                           hover:bg-opacity-90 transition flex items-center justify-center
                           disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === 'submitting' ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}